import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import StatisticStatusBar from './StatisticStatusBar';
import { StatisticPageLoadingSkeleton } from './StatisticPageSkeleton';
import AuthContext from '../../context/authContext';
import { getStatisticViewShellFilterPaperSx } from '../../util/chartTheme';

const applyButtonSx = {
  minHeight: 56,
  borderRadius: 2,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.95rem',
  flex: { xs: '1 1 100%', sm: '0 0 auto' },
  minWidth: { xs: 0, sm: 160 },
  px: { sm: 4 },
};

const pageContainerSx = {
  width: '100%',
  maxWidth: { xs: '100%', lg: 1440 },
  mx: 'auto',
  px: { xs: 1.5, sm: 2, md: 3 },
  py: { xs: 2, sm: 3 },
  boxSizing: 'border-box',
};

// Merge caller-provided DatePicker slotProps with our defaults (fullWidth
// textField + Enter-to-apply keyboard handler). The caller's onKeyDown (if
// any) still runs first; if it doesn't call preventDefault, Enter triggers
// the shell's apply handler so the user never commits with stale dates.
const mergeDatePickerSlotProps = (callerSlotProps, onEnterApply) => {
  const callerTextField = callerSlotProps?.textField || {};
  const callerOnKeyDown = callerTextField.onKeyDown;
  return {
    ...(callerSlotProps || {}),
    textField: {
      fullWidth: true,
      ...callerTextField,
      onKeyDown: e => {
        if (callerOnKeyDown) callerOnKeyDown(e);
        if (!e.defaultPrevented && e.key === 'Enter') {
          e.preventDefault();
          onEnterApply();
        }
      },
    },
  };
};

// A single uncontrolled DatePicker. The MUI `DatePicker` manages its own
// internal text + value state; we never touch React state while the user
// is typing. `onChange` only writes into a ref so the surrounding
// component does NOT re-render on keystrokes.
//
// When the external value changes (parent reset, cache-entry load), we
// remount the picker via `key` so it picks up the new default. This is
// the cheapest way to force MUI X DatePickers to accept a new
// "uncontrolled" value without switching to controlled mode.
const UncontrolledDatePicker = React.memo(function UncontrolledDatePicker({
  externalValue,
  label,
  pickerProps,
  onChangeRef,
  onEnterApply,
}) {
  // Remount counter: bumped whenever externalValue changes to a different
  // calendar day compared to the last mount.
  const mountedValueRef = React.useRef(externalValue);
  const [mountKey, setMountKey] = React.useState(0);

  React.useEffect(() => {
    const prev = mountedValueRef.current;
    const sameDay =
      (prev == null && externalValue == null) ||
      (prev &&
        externalValue &&
        typeof externalValue.isSame === 'function' &&
        externalValue.isSame(prev, 'day'));
    if (sameDay) return;
    mountedValueRef.current = externalValue;
    if (onChangeRef) onChangeRef.current = externalValue;
    setMountKey(k => k + 1);
  }, [externalValue, onChangeRef]);

  const handleChange = React.useCallback(
    value => {
      if (onChangeRef) onChangeRef.current = value;
    },
    [onChangeRef]
  );

  const slotProps = React.useMemo(
    () => mergeDatePickerSlotProps(pickerProps?.slotProps, onEnterApply),
    [pickerProps, onEnterApply]
  );

  return (
    <DatePicker
      key={mountKey}
      label={label}
      defaultValue={mountedValueRef.current}
      onChange={handleChange}
      {...(pickerProps || {})}
      slotProps={slotProps}
    />
  );
});

// Self-contained date range inputs. Both DatePickers are fully
// uncontrolled (see UncontrolledDatePicker) so that typing into either
// field triggers NO React re-renders anywhere — not in this component,
// not in the shell, and not in the parent statistic view. The latest
// typed values are written into the shell's refs so the apply handler
// can read them on click.
const DateRangeInputs = React.memo(function DateRangeInputs({
  externalStart,
  externalEnd,
  startLabel,
  endLabel,
  startPickerProps,
  endPickerProps,
  onStartChangeRef,
  onEndChangeRef,
  onEnterApply,
}) {
  return (
    <>
      <UncontrolledDatePicker
        externalValue={externalStart}
        label={startLabel}
        pickerProps={startPickerProps}
        onChangeRef={onStartChangeRef}
        onEnterApply={onEnterApply}
      />
      <UncontrolledDatePicker
        externalValue={externalEnd}
        label={endLabel}
        pickerProps={endPickerProps}
        onChangeRef={onEndChangeRef}
        onEnterApply={onEnterApply}
      />
    </>
  );
});

/**
 * Unified shell for all statistic views.
 * Renders: page container, filter bar (date range + optional branch + extras + apply),
 * StatisticStatusBar (refresh button, cache entry picker, job states).
 * View-specific content is rendered as children beneath the status bar.
 */
const StatisticViewShell = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDateLabel = 'Startdatum',
  endDateLabel = 'Enddatum',
  startDatePickerProps,
  endDatePickerProps,

  branchOptions = null,
  selectedBranchId = '',
  onBranchChange,
  branchSelectLabel = 'Standort',
  branchSelectDisabled = false,
  branchSelectIdPrefix = 'statistic-branch',

  extraFilters = null,

  onApply,
  applyDisabled = false,
  applyLabel = 'Anwenden',
  applyExtraActions = null,
  // When true, the Apply button is rendered inline in the filter grid
  // as its own slot (next to the date pickers / extras) and the separate
  // bottom apply row is suppressed. Only useful for compact views with
  // a small number of filters (e.g. BonStats: just two date pickers).
  applyInFilterRow = false,

  status,
  isStale,
  cacheComputedAt,
  cacheExpiresAt,
  job,
  error,
  cacheEntries,
  selectedEntryId,
  onSelectEntry,
  entriesLoading,
  onRefresh,
  refreshDisabled,
  filterParamLookups,
  filterParamConfig,

  headerSlot = null,
  beforeStatusBar = null,
  loading = false,
  loadingSkeleton = null,
  hasPayload,
  externallyLoading = false,
  children,
}) => {
  // Unified "should we render the skeleton or the children" rule. A view
  // can either:
  //   - pass an explicit `loading` boolean (legacy), or
  //   - pass `hasPayload` + `status` (+ optional `externallyLoading` for
  //     outer prerequisites like branch lists loading) and let the shell
  //     decide.
  // When `hasPayload` is provided, we hold the skeleton until the
  // payload has actually arrived, so no view ever briefly flashes
  // "Keine Daten" during idle/pending/running transitions.
  const derivedLoading =
    externallyLoading ||
    (hasPayload === false &&
      status !== 'failed' &&
      status !== 'fresh' &&
      status !== 'stale');
  const effectiveLoading = hasPayload === undefined ? loading : derivedLoading;
  const authCtx = React.useContext(AuthContext);
  const theme = useTheme();
  const filterPaperSx = React.useMemo(() => getStatisticViewShellFilterPaperSx(theme), [theme]);
  const currentUserId = authCtx?.currentUser?.userId || null;
  const resolvedLookups = React.useMemo(() => {
    const base = {};
    if (Array.isArray(branchOptions) && branchOptions.length > 0) {
      const branchMap = {};
      for (const o of branchOptions) {
        if (!o) continue;
        const id = o._id == null ? '' : String(o._id);
        if (id === '') branchMap.all = o.label;
        else branchMap[id] = o.label;
      }
      base.branchScope = branchMap;
    }
    if (filterParamLookups && typeof filterParamLookups === 'object') {
      for (const [key, value] of Object.entries(filterParamLookups)) {
        base[key] = { ...(base[key] || {}), ...(value || {}) };
      }
    }
    return base;
  }, [branchOptions, filterParamLookups]);
  // "Alle Standorte" is not a choice of its own. With a single real branch the
  // select costs a slot of the filter row and cannot change anything, so it is
  // left out entirely and that branch is applied instead.
  const realBranchOptions = React.useMemo(
    () =>
      (Array.isArray(branchOptions) ? branchOptions : []).filter(o => {
        const id = o?._id == null ? '' : String(o._id);
        return id !== '' && id !== '__all__' && id !== 'all';
      }),
    [branchOptions]
  );
  const showBranchSelect = realBranchOptions.length > 1;
  const onlyBranchId = realBranchOptions.length === 1 ? String(realBranchOptions[0]._id) : null;

  React.useEffect(() => {
    if (!onlyBranchId || !onBranchChange) return;
    if (!selectedBranchId) onBranchChange(onlyBranchId);
  }, [onlyBranchId, selectedBranchId, onBranchChange]);
  const extraFilterNodes = Array.isArray(extraFilters)
    ? extraFilters.filter(Boolean)
    : extraFilters
      ? [extraFilters]
      : [];

  const filterSlots =
    2 +
    (showBranchSelect ? 1 : 0) +
    extraFilterNodes.length +
    (applyInFilterRow ? 1 : 0);

  const xlColumns = Math.min(Math.max(filterSlots, 2), 4);
  const lgColumns = Math.min(xlColumns, 4);
  const mdColumns = Math.min(xlColumns, 3);

  // Keep the currently-displayed DatePicker values in *refs* (no state
  // here). The DatePickers themselves live in a memoized subcomponent
  // that manages its own local state, so typing into the field does NOT
  // re-render this shell (nor the heavy parent statistic view). On
  // apply, we read the latest values from the refs and only then push
  // them up to the parent.
  const draftStartRef = React.useRef(startDate);
  const draftEndRef = React.useRef(endDate);

  const handleApplyClick = React.useCallback(() => {
    const nextStart = draftStartRef.current;
    const nextEnd = draftEndRef.current;
    const startIsValid = nextStart && nextStart.isValid && nextStart.isValid();
    const endIsValid = nextEnd && nextEnd.isValid && nextEnd.isValid();

    if (!startIsValid || !endIsValid) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }
    if (nextStart.isAfter(nextEnd)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }
    if (
      onStartDateChange &&
      (!startDate ||
        !nextStart ||
        typeof nextStart.isSame !== 'function' ||
        !nextStart.isSame(startDate))
    ) {
      onStartDateChange(nextStart);
    }
    if (
      onEndDateChange &&
      (!endDate ||
        !nextEnd ||
        typeof nextEnd.isSame !== 'function' ||
        !nextEnd.isSame(endDate))
    ) {
      onEndDateChange(nextEnd);
    }
    if (onApply) onApply(nextStart, nextEnd);
  }, [startDate, endDate, onStartDateChange, onEndDateChange, onApply]);

  return (
    <Box sx={pageContainerSx}>
      {headerSlot}
      <Paper elevation={0} sx={filterPaperSx}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: `repeat(${mdColumns}, minmax(0, 1fr))`,
              lg: `repeat(${lgColumns}, minmax(0, 1fr))`,
              xl: `repeat(${xlColumns}, minmax(0, 1fr))`,
            },
            alignItems: 'end',
          }}
        >
          <DateRangeInputs
            externalStart={startDate}
            externalEnd={endDate}
            startLabel={startDateLabel}
            endLabel={endDateLabel}
            startPickerProps={startDatePickerProps}
            endPickerProps={endDatePickerProps}
            onStartChangeRef={draftStartRef}
            onEndChangeRef={draftEndRef}
            onEnterApply={handleApplyClick}
          />

          {showBranchSelect && (
            <FormControl fullWidth sx={{ minWidth: 0 }}>
              <InputLabel id={`${branchSelectIdPrefix}-label`} shrink>
                {branchSelectLabel}
              </InputLabel>
              <Select
                labelId={`${branchSelectIdPrefix}-label`}
                label={branchSelectLabel}
                notched
                displayEmpty
                value={selectedBranchId}
                onChange={e => onBranchChange && onBranchChange(e.target.value)}
                disabled={branchSelectDisabled}
              >
                {branchOptions.map(o => (
                  <MenuItem key={o._id || 'all'} value={o._id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {extraFilterNodes.map((node, idx) => (
            <Box key={idx} sx={{ minWidth: 0 }}>
              {node}
            </Box>
          ))}

          {applyInFilterRow && (
            <Button
              variant='contained'
              onClick={handleApplyClick}
              disabled={applyDisabled}
              sx={{ ...applyButtonSx, width: '100%', flex: '1 1 100%' }}
            >
              {applyLabel}
            </Button>
          )}
        </Box>

        {!applyInFilterRow && (
          <Box
            sx={{
              mt: { xs: 1.5, sm: 2 },
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
            }}
          >
            {applyExtraActions}
            <Button
              variant='contained'
              onClick={handleApplyClick}
              disabled={applyDisabled}
              sx={applyButtonSx}
            >
              {applyLabel}
            </Button>
          </Box>
        )}
        {applyInFilterRow && applyExtraActions && (
          <Box
            sx={{
              mt: { xs: 1.5, sm: 2 },
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
            }}
          >
            {applyExtraActions}
          </Box>
        )}
      </Paper>

      {beforeStatusBar}

      <StatisticStatusBar
        status={status}
        isStale={isStale}
        cacheComputedAt={cacheComputedAt}
        cacheExpiresAt={cacheExpiresAt}
        job={job}
        error={error}
        entries={cacheEntries}
        selectedEntryId={selectedEntryId}
        onSelectEntry={onSelectEntry}
        entriesLoading={entriesLoading}
        onRefresh={onRefresh}
        refreshDisabled={refreshDisabled}
        filterParamLookups={resolvedLookups}
        filterParamConfig={filterParamConfig}
        currentUserId={currentUserId}
      />

      {effectiveLoading ? (loadingSkeleton || <StatisticPageLoadingSkeleton />) : children}
    </Box>
  );
};

export default StatisticViewShell;
